import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Head } from "vite-react-ssg";
import NavigationButtons from "@/components/NavigationButtons";
import RelatedLinks from "@/components/RelatedLinks";

// NOTE: Slug retained (/blog/deepseek-agi-10-billion-funding-round) for URL stability
// and inbound links. The originally rumored $10B target ultimately closed at
// ~$7.4B — title, body and schema below reflect the actual outcome.
const canonical = "https://deepseek.ai/blog/deepseek-agi-10-billion-funding-round";
const title = "DeepSeek's AGI Funding Round: From $10B Rumor to ~$7.4B Close";
const description =
  "DeepSeek's frontier-AGI funding round: initial reports pointed to $10B, the round ultimately closed near $7.4B. Investors, thesis, and what it means for open-weight AI.";

const html = `
<p><em>Disclaimer: deepseek.ai is an independent community and fan site, not officially affiliated with DeepSeek AI. Figures below are drawn from Bloomberg, TechNode, Pandaily and CnEVPost reporting and remain subject to confirmation.</em></p>

<h2>Round Timeline: $10B Rumor → ~$7.4B Close</h2>
<p>The story evolved substantially between May and July 2026:</p>
<ul>
  <li><strong>22 May 2026 — Bloomberg:</strong> reports the round is "advancing" toward roughly $10B, with founder Liang Wenfeng framing the raise around Artificial General Intelligence rather than enterprise revenue.</li>
  <li><strong>4 June 2026 — TechNode:</strong> negotiations reported at around $7B, with Tencent and <a href="/blog/leak-catl-deepseek-investor">CATL</a> named as backers.</li>
  <li><strong>Mid-June 2026:</strong> the round <strong>closed</strong> at approximately <strong>50 billion yuan (~$7.4B)</strong>, valuing DeepSeek near $50B. The "50 billion yuan round" and the "$7.4B round" are the same financing in two currencies. See our deep-dives on the <a href="/blog/deepseek-50-billion-funding-round">50 billion yuan structure</a> and the <a href="/blog/deepseek-raises-7-4-billion-bizarre-deal">founder-centric deal geometry</a>.</li>
  <li><strong>25 July 2026 — Bloomberg:</strong> a <em>second</em> round, reportedly targeting a <strong>$74B valuation</strong>, was <strong>paused</strong>.</li>
</ul>
<p>The original $10B number reflected the fundraising target, not the final print. A lab that opens with a $10B ask and closes at ~$7.4B is still doing an extraordinary round by any historical measure, but the pitch of "AGI at any price" was moderated by real due diligence.</p>

<h2>Who Put In What</h2>
<p>The most unusual feature of the round is the founder's own participation. Reported allocations:</p>
<ul>
  <li><strong>Liang Wenfeng (founder):</strong> 20 billion yuan — roughly <strong>40% of the entire round</strong>.</li>
  <li><strong>Tencent:</strong> 10 billion yuan.</li>
  <li><strong>CATL:</strong> 5 billion yuan (~$740M).</li>
  <li><strong>NetEase:</strong> 3 billion yuan.</li>
  <li><strong>JD.com:</strong> 3 billion yuan.</li>
  <li><strong>Remaining balance:</strong> spread across additional domestic strategic and financial investors.</li>
</ul>
<p>External investors are reported to hold <strong>no voting rights</strong> and to be bound by a <strong>five-year lock-up</strong>. Liang retains control with a reported ~39% stake. In other words: the capital arrived, the governance did not change.</p>

<h2>AGI as a Fundraising Thesis</h2>
<p>Most frontier labs avoid the AGI word in investor decks. Western counterparts prefer <em>"enterprise AI"</em> or <em>"productivity tooling"</em> — language that is easier to underwrite. DeepSeek did the opposite, and that framing matters:</p>
<ol>
  <li><strong>Long-horizon capital.</strong> An AGI pitch filters out investors chasing 24-month ARR curves and attracts patient capital for multi-year research bets.</li>
  <li><strong>Open-weight credibility.</strong> Continuing open releases — the same strategy behind <a href="/blog/deepseek-r2-ai-model-launch-2025">R2</a> and the <a href="/blog/deepseek-v4-compressed-attention">V4 compressed-attention</a> work — only makes sense if monetisation is explicitly deprioritised.</li>
  <li><strong>Governance without dilution of control.</strong> No voting rights plus a five-year lock-up means the research agenda stays where it was.</li>
</ol>

<h2>Why Investors Believed</h2>
<p>Through most of 2023 and 2024, foreign capital treated DeepSeek as a curiosity. Low-cost open models forced the industry to reconsider training economics. The question stopped being <em>"can a Chinese lab catch up?"</em> and became <em>"do you actually need OpenAI-scale spending to reach the frontier?"</em></p>
<ul>
  <li><a href="/blog/deepseek-v4-unveiled-1-6-trillion-parameters">V4's 1.6T-parameter MoE architecture</a> demonstrating efficient sparse compute.</li>
  <li>Aggressive cost-per-token undercutting on the <a href="/pricing">DeepSeek API</a>.</li>
  <li>Sustained open-weight releases turning the community into a free distribution and validation network.</li>
</ul>


<h2>How DeepSeek Differs from Western Labs</h2>
<table style="width:100%;border-collapse:collapse;margin:1.5rem 0;">
  <thead>
    <tr style="background:#f3f4f6;text-align:left;">
      <th style="padding:0.75rem;border:1px solid #e5e7eb;">Theme</th>
      <th style="padding:0.75rem;border:1px solid #e5e7eb;">DeepSeek Positioning</th>
    </tr>
  </thead>
  <tbody>
    <tr><td style="padding:0.75rem;border:1px solid #e5e7eb;">Monetisation</td><td style="padding:0.75rem;border:1px solid #e5e7eb;">Explicitly secondary to research</td></tr>
    <tr><td style="padding:0.75rem;border:1px solid #e5e7eb;">Model access</td><td style="padding:0.75rem;border:1px solid #e5e7eb;">Open-source / open-weight emphasis</td></tr>
    <tr><td style="padding:0.75rem;border:1px solid #e5e7eb;">Capital strategy</td><td style="padding:0.75rem;border:1px solid #e5e7eb;">Self-funded historically, now scaling externally</td></tr>
    <tr><td style="padding:0.75rem;border:1px solid #e5e7eb;">Stated ambition</td><td style="padding:0.75rem;border:1px solid #e5e7eb;">AGI</td></tr>
    <tr><td style="padding:0.75rem;border:1px solid #e5e7eb;">Competitive angle</td><td style="padding:0.75rem;border:1px solid #e5e7eb;">Efficient training, lower cost per FLOP of capability</td></tr>
  </tbody>
</table>

<h2>What to Watch Next</h2>
<ul>
  <li><strong>The paused second round.</strong> Bloomberg reported on 25 July 2026 that a follow-on raise at a ~$74B valuation was put on hold. Whether it restarts, and at what price, is the clearest signal on how investors currently value the lab.</li>
  <li><strong>Next model release:</strong> a raise of this size is typically followed within ~6 months by a flagship model — see our <a href="/deepseek-v4">V4 tracker</a>.</li>
  <li><strong>Pricing behaviour:</strong> the <a href="/blog/deepseek-v4-pro-api-price-cut-permanent">permanent V4-Pro price cut</a> and the <a href="/blog/deepseek-chat-reasoner-retired-billing-impact">retirement of the legacy model IDs</a> show how the lab is now managing serving cost against capital.</li>
</ul>


<h2>Bottom Line</h2>
<p>DeepSeek didn't raise ~$7.4B to ship a better chatbot. Even at the lower-than-rumoured close, the pitch — pursue AGI, stay open-weight, prioritise research over revenue — reframes what the next phase of the frontier-AI race looks like. Whether the AGI framing holds, the round confirms that the frontier is no longer a one-continent story.</p>
`;

const schemaData = {
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: title,
  datePublished: "2026-05-24T03:28:43+00:00",
  dateModified: "2026-07-25T00:00:00+00:00",
  author: { "@type": "Organization", name: "The Independent DeepSeek Guide", url: "https://deepseek.ai" },
  publisher: {
    "@type": "Organization",
    name: "The Independent DeepSeek Guide",
    logo: { "@type": "ImageObject", url: "https://deepseek.ai/logo.png" },
  },
  description,
  mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
};

const DeepseekAgiFundingRound = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background">
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Head>
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <Button variant="ghost" onClick={() => navigate("/blog")} className="mb-6">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Blog
        </Button>
        <h1 className="text-4xl md:text-5xl font-bold mb-6">
          DeepSeek's AGI Funding Round: From $10B Rumor to ~$7.4B Close
        </h1>
        <p className="text-muted-foreground mb-8">Published 24 May 2026 · Updated 25 July 2026 with the closed round figures</p>
        <article
          className="prose prose-lg max-w-none"
          dangerouslySetInnerHTML={{ __html: html }}
        />
        <NavigationButtons />
        <RelatedLinks />
      </main>
    </div>
  );
};

export default DeepseekAgiFundingRound;
